import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import PageLayout from '../shell/PageLayout';
import { accentCardSx, accentTextSx } from '../common/theme/accents';
import { useZeny } from '../attention/ZenyProvider';
import IssueTable from '../attention/IssueTable';
import { hoursAndMinutes } from '../operations/stageWords';
import { request, useOperations } from '../operations/OperationsProvider';
import OperationsStatus from '../operations/OperationsStatus';
import BriefingPanel from '../operations/BriefingPanel';
import ScopeFilters, { useScope, inDates } from '../operations/ScopeFilters';
import AreaMovementPanels, { pace } from './AreaMovementPanels';
import DashboardTiles from './DashboardTiles';
import PerformanceCharts from '../operations/PerformanceCharts';
import { callInQueue, humanMs } from '../operations/areas';
import DownloadTable from '../shell/DownloadTable';
import { plantMidnight } from '../common/util/datetime';
import DesktopAlertSwitch from '../attention/DesktopAlertSwitch';

// The same counts the server publishes, over a chosen subset of trips. Definitions follow the
// server's exactly: a filtered tile and an unfiltered one have to mean the same thing.
const countsFor = (rows, analytics) => {
  const midnight = plantMidnight();
  const today = (at) => !!at && Date.parse(at) >= midnight;
  const open = rows.filter((t) => !t.closedAt);
  const standing = (trip) => {
    const last = [...(trip.detections || [])].reverse().find((detection) => !detection.deviation);
    return last ? (trip.checkpoints || []).find((c) => c.id === last.checkpointId) : null;
  };
  const longStayMs = analytics?.longStayMs || 0;
  return {
    activeTrips: open.length,
    inPlant: open.filter((t) => t.phase === 'IN_PLANT').length,
    inTransit: open.filter((t) => t.phase === 'IN_TRANSIT').length,
    openedToday: rows.filter((t) => today(t.createdAt)).length,
    closedToday: rows.filter((t) => today(t.closedAt)).length,
    gateOutToday: rows.filter((t) => today(t.gateOut)).length,
    arrivedToday: rows.filter((t) => t.closureReason === 'DESTINATION_REACHED' && today(t.closedAt)).length,
    atBay: open.filter((t) => standing(t)?.type === 'BAY').length,
    longStay: longStayMs > 0 ? open.filter((t) => t.gateIn && !t.gateOut && Date.now() - Date.parse(t.gateIn) > longStayMs).length : 0,
    longStayMs,
  };
};

const OverviewPage = ({ exceptionsOnly = false, view = 'plant' }) => {
  const navigate = useNavigate(); const scope = useScope();
  const betweenSitesView = view === 'areas';
  const { items, error: issueError, updatedAt } = useZeny();
  const { trips, data, speed, analytics, error } = useOperations();
  const inScope = trips.filter((t) => scope.matches(t));
  // On the between-sites view, every figure on the page is the captive fleet's - so the charts are
  // too. Showing the plant's trips under the fleet's tiles invited anybody reading down the page to
  // take the second as a breakdown of the first.
  const captiveIds = new Set((data.vehicles || []).filter((v) => v.fleet === 'CAPTIVE').map((v) => v.id));
  const selected = betweenSitesView ? inScope.filter((t) => captiveIds.has(t.vehicleId)) : inScope;
  // An exception belongs to the selection when the trip it was raised on does. The old rule checked
  // site, vehicle, route, movement and direction and quietly ignored transporter, material, origin,
  // destination and area - so the tiles narrowed to one carrier while the alert list beneath them
  // still showed everybody's. The trips have already been matched once, above; matching through them
  // means the two can never again disagree.
  // Matched without the date range: an exception raised this morning on a trip opened last week is
  // this morning's exception, and cutting its trip by the creation date would lose it.
  const chosen = new Set(trips.filter((t) => scope.matches(t, false)).map((t) => t.id));
  // Filters that ask about a load - who carried it, what it was, where it came from and went - are
  // questions an exception with no trip cannot answer, so under those it is not in the selection.
  const aboutALoad = ['route', 'movement', 'direction', 'transporter', 'material', 'origin', 'destination', 'area'].some((key) => scope[key]);
  const issues = items.filter((i) => inDates(i.occurredAt || i.raisedAt, scope.from, scope.to) && (
    i.tripId
      ? chosen.has(i.tripId)
      : !aboutALoad && (!scope.vehicle || String(i.vehicleId) === scope.vehicle) && (!scope.site || i.site === scope.site)
  ));
  // Outstanding urgent exceptions the date range leaves out. The rail's count is of everything
  // outstanding, so without this the two numbers differ with nothing on screen saying why.
  const urgentOpen = (i) => ['HIGH', 'CRITICAL'].includes(i.severity) && i.status !== 'RESOLVED';
  const older = exceptionsOnly
    ? items.filter((i) => urgentOpen(i) && !inDates(i.occurredAt || i.raisedAt, scope.from, scope.to)).length
    : 0;
  const durations = selected.filter((t) => t.gateIn && t.gateOut).map((t) => (Date.parse(t.gateOut) - Date.parse(t.gateIn)) / 60000);
  const areas = data.areas || [];
  const queue = callInQueue(areas, trips, data.vehicles).filter((q) => !scope.area || areas.some((a) => String(a.id) === scope.area && a.name === q.area));
  const meanTurnaround = durations.length
    ? hoursAndMinutes(durations.reduce((a, b) => a + b, 0) / durations.length) : '—';
  // Counted on the server, so both dashboards read the same figures and "today" means midnight
  // where the plant is rather than wherever the browser happens to be.
  //
  // Those figures are the whole plant's. Ask about one transporter, one material, one origin or one
  // destination and they would be the wrong answer told confidently - so the same counts are taken
  // again over the trips that match, by the same definitions, from what this page already holds.
  // Nothing is recomputed while nobody is asking: with no such filter set, the server's figures stand.
  // Any filter at all, not only the four about the load. Recomputing for a transporter but not for
  // a site meant the same screen sometimes answered for the selection and sometimes for the whole
  // plant, with nothing on it saying which - and the figures changed meaning the moment a second
  // filter was added. Either the selection is honoured everywhere or it is honoured nowhere.
  const narrowing = ['site', 'route', 'vehicle', 'movement', 'direction', 'area', 'transporter', 'material', 'origin', 'destination'];
  const narrowed = narrowing.some((key) => scope[key]);
  const counts = narrowed ? countsFor(trips.filter((t) => scope.matches(t, false)), analytics) : (analytics || {});
  const outbound = view === 'outbound';
  // Worked out on the scheduler and read here, because these figures replay every finished run of
  // the last fortnight. Only fetched for the view that shows them.
  const [day, setDay] = React.useState(null);
  // Why this dashboard is not being shown, where the reason is that this account may not see it.
  const [denied, setDenied] = React.useState(null);
  React.useEffect(() => {
    if (!betweenSitesView) return undefined;
    let live = true;
    const pull = () => request('/api/operations/area-movement')
      .then((held) => { if (live) { setDay(held); setDenied(null); } })
      // Only a refusal is reported. Anything else is a feed that did not load, which the rest of
      // the page already handles and which must not be presented as a permission.
      .catch((err) => { if (live && err?.status === 403) setDenied(err.message); });
    pull();
    const timer = setInterval(pull, 60000);
    return () => { live = false; clearInterval(timer); };
  }, [betweenSitesView]);
  // A tile counted under a filter opens a list under the same filter, or the reader is handed a
  // number for one transporter and a list for all of them.
  const carried = narrowing
    .filter((key) => scope[key])
    .map((key) => `&${key}=${encodeURIComponent(scope[key])}`)
    .join('');
  // A tile that counts what is happening now opens the map, which shows exactly those trucks. Under
  // a filter the map cannot narrow itself, so the same count opens the filtered list instead - the
  // promise is the records, not the picture.
  const livePlace = (map, phase) => (narrowed ? `/trips?status=ACTIVE&phase=${phase}${carried}` : map);
  const longStayHours = Math.round(((counts.longStayMs || 0) / 3600000) * 10) / 10;
  const plantTiles = [
    { title: 'Opened today', value: counts.openedToday ?? 0, accent: 'teal', to: `/trips?today=opened${carried}`, hint: 'Trips started since midnight' },
    { title: 'Closed today', value: counts.closedToday ?? 0, accent: 'indigo', to: `/trips?today=closed${carried}`, hint: 'Finished since midnight' },
    { title: 'In plant now', value: counts.inPlant ?? 0, accent: 'violet', to: livePlace('/plant-tracking', 'IN_PLANT') },
    // Replaces a second count of much the same trucks with what the plant is doing to them.
    { title: 'Loading / unloading', value: counts.atBay ?? 0, accent: 'amber', to: `/trips?standing=BAY${carried}`, hint: 'Standing at a bay' },
    ...(longStayHours > 0
      ? [{ title: `Inside over ${longStayHours} h`, value: counts.longStay ?? 0, accent: 'rose', to: `/trips?inside=long${carried}`, hint: 'Set under Operational rules' }]
      : []),
    ...(areas.length > 0
      ? [{ title: 'Waiting outside', value: queue.length, accent: 'sky', to: `/parking${scope.area ? `?area=${encodeURIComponent(scope.area)}` : ''}`, hint: 'All parking occupants · area filter only' }]
      : []),
  ];
  const outboundTiles = [
    { title: 'On the road now', value: counts.inTransit ?? 0, accent: 'teal', to: livePlace('/outbound-tracking', 'IN_TRANSIT') },
    { title: 'Despatched today', value: counts.gateOutToday ?? 0, accent: 'indigo', to: `/trips?today=despatched${carried}`, hint: 'Through the gate since midnight' },
    { title: 'Arrived today', value: counts.arrivedToday ?? 0, accent: 'violet', to: `/trips?today=arrived${carried}`, hint: 'Recorded at the customer' },
    { title: 'Open trips', value: counts.activeTrips ?? 0, accent: 'amber', to: `/trips?status=ACTIVE${carried}` },
  ];
  // Movement between sites: a different fleet doing a different job. No gate, no challan, no
  // turnaround - it repeats a cycle, so every tile carries what to measure today against.
  //
  // Deliberately absent: kilometres and average speed, which on a fixed corridor are near constant
  // and drive no decision; and loaded-distance utilisation, which would peg a shuttle near half for
  // ever - it runs loaded out and empty back by design - and report a failure that is not one.
  const onTimeShare = day?.judgedToday > 0 ? Math.round((day.onTimeToday / day.judgedToday) * 100) : null;
  const paced = pace(day?.runsToday ?? 0, day?.expectedByNow ?? 0);
  const areaTiles = [
    {
      title: 'Trips today',
      value: day?.runsToday ?? 0,
      accent: paced && !paced.good ? 'amber' : 'teal',
      to: '/trips?today=closed',
      hint: paced ? paced.text : 'Finished since midnight',
    },
    // The shuttle's headline operational number, and the only one that says whether the fleet is
    // having a slow day as opposed to a quiet one.
    ...(day?.cycleMs
      ? [{
        title: 'Round trip time',
        value: humanMs(day.cycleMs),
        accent: day.usualCycleMs && day.cycleMs > day.usualCycleMs ? 'rose' : 'indigo',
        to: '/between-areas',
        hint: day.usualCycleMs ? `Usually ${humanMs(day.usualCycleMs)}` : 'Gate to gate of one round trip',
      }]
      : []),
    // A rate, not a count: three late out of four is a different day from three out of forty.
    ...(onTimeShare != null
      ? [{
        title: 'On time',
        value: `${onTimeShare}%`,
        accent: onTimeShare >= 80 ? 'teal' : 'rose',
        to: '/between-areas',
        hint: `${day.onTimeToday} of ${day.judgedToday} trips within the usual`,
      }]
      : []),
    // The number that names the fix rather than only reporting the damage.
    ...(day?.lostMs > 0
      ? [{
        title: 'Time lost today',
        value: humanMs(day.lostMs),
        accent: 'amber',
        to: '/between-areas',
        hint: day.worstLeg ? `Most of it at ${day.worstLeg}` : 'Against what each leg normally takes',
      }]
      : []),
    { title: 'On trip now', value: day?.running ?? 0, accent: 'violet', to: '/trips?status=ACTIVE' },
    // The one that costs money on a dedicated fleet: a lorry nobody sent anywhere.
    { title: 'Idle', value: day?.idle ?? 0, accent: 'sky', to: '/vehicles', hint: `Of ${day?.fleet ?? 0} marked captive` },
  ];
  const tiles = betweenSitesView ? areaTiles : outbound ? outboundTiles : plantTiles;
  return (
    <PageLayout
      title={exceptionsOnly ? 'Exceptions' : betweenSitesView ? 'Area movement' : outbound ? 'Outbound operations' : 'In-plant operations'}
      subtitle={exceptionsOnly
        ? 'Review, acknowledge and resolve operational exceptions.'
        : betweenSitesView
          ? 'Trucks running between your own sites: trips turned, and where the time went.'
          : outbound
            ? 'Loads that have left the plant: on the road, despatched and arrived.'
            : 'What the plant is working on now, and what is holding it up.'}
      actions={(
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          {exceptionsOnly && <DesktopAlertSwitch />}
          {!exceptionsOnly && <Button variant="text" onClick={() => navigate('/vehicles')}>Vehicle list</Button>}
          <Button variant="outlined" onClick={() => navigate(exceptionsOnly ? '/analytics' : outbound ? '/outbound-tracking' : '/live')}>
            {exceptionsOnly ? 'View analytics' : outbound ? 'Track outbound' : 'Open live tracking'}
          </Button>
        </Stack>
      )}
    >
      <OperationsStatus />
      {!exceptionsOnly && !(betweenSitesView && denied) && <Typography variant="h6" sx={{ mb: 1 }}>{outbound ? 'On the road today' : 'In the plant today'}</Typography>}
      {/* Shown instead of the tiles, not beside them. Every figure here is the whole captive
          fleet's, so there is no part of this dashboard that is this account's to read, and
          rendering it alongside an explanation would invite somebody to read the zeros anyway. */}
      {betweenSitesView && denied && <Alert severity="info" sx={{ mb: 3 }}>{denied}</Alert>}
      {!exceptionsOnly && !(betweenSitesView && denied) && <DashboardTiles tiles={tiles} unavailable={!!error} />}
      {/* Exceptions are not repeated here. They have one home - the exceptions page - reached by
          the bell when something arrives, by the count beside Today, and by the alert itself. A
          dashboard that also listed them gave the same facts a second voice, and two lists that
          differ by a filter are worse than one list that does not. */}
      <ScopeFilters scope={scope} dateLabel="Trip creation / issue occurrence date" />
      {exceptionsOnly && (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Alerts</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{issueError || !updatedAt ? 'Issue count unavailable' : `${issues.filter((i) => ['HIGH', 'CRITICAL'].includes(i.severity) && i.status !== 'RESOLVED').length} urgent in this selection · Includes acknowledged issues`}</Typography>
        {/* The page opens on a date range like every other page, which means an exception raised
            before it is not in the list - and the count on the rail, which counts what is
            outstanding whenever it was raised, would then be a larger number with no explanation on
            screen. So the difference is stated, with the one thing to do about it. */}
        {older > 0 && (
          <Alert severity="info" sx={{ mb: 2 }} action={<Button size="small" onClick={scope.allDates}>Show all dates</Button>}>
            {older === 1 ? '1 urgent exception is still open from before this date range.' : `${older} urgent exceptions are still open from before this date range.`}
          </Alert>
        )}
        <IssueTable items={issues} />
      </Paper>
      )}
      {/* Where the cycle goes, and which lorry turned how many. Below the tiles because the tiles
          say whether today is going well and these say what to do about it. */}
      {betweenSitesView && !denied && <AreaMovementPanels day={day} />}
      {/* The refusal above is scoped to the captive dashboard, which is the only thing it describes.
          The routes are keyed so leaving this view remounts the page and clears it, but the render
          does not rely on that: without the guard, dropping a key from the route table would blank
          the in-plant dashboard for a haulier who had looked at area movement first. */}
      {/* Turnaround is gate in to gate out. A fleet that passes no gate has none, so the tile is
          not shown rather than shown as a dash somebody has to interpret. */}
      {!exceptionsOnly && !betweenSitesView && (
        <Paper variant="outlined" sx={[{ p: 2, mb: 3 }, accentCardSx('indigo')]}>
          <Typography variant="subtitle2">Selected period · Mean turnaround</Typography>
          <Typography variant="h5" sx={accentTextSx('indigo')}>{error ? '—' : meanTurnaround}</Typography>
          <Typography variant="caption" color="text.secondary">Based on trips created in the date range above with both Gate In and Gate Out recorded.</Typography>
        </Paper>
      )}
      {/* Zeny's briefing now carries the captive fleet's figures too - trips turned against usual,
          time lost and where, and any truck that stood all day - so it belongs on this page as much
          as on the plant's. */}
      {!exceptionsOnly && !outbound && <BriefingPanel />}
      {!exceptionsOnly && !outbound && !betweenSitesView && areas.length > 0 && (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Waiting to be called in</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Vehicles standing in an outside parking area, longest wait first.</Typography>
        {queue.length === 0 ? (
          <Typography color="text.secondary">No vehicles are parked outside right now.</Typography>
        ) : (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <DownloadTable
                name="waiting-to-be-called-in"
                rows={queue}
                columns={[
                  ['Vehicle', (q) => q.vehicle],
                  ['Parking area', (q) => q.area],
                  ['Waiting', (q) => humanMs(q.waitingMs)],
                  ['Last seen by', (q) => (q.byReader ? 'Reader' : q.stale ? 'Tracker (gone quiet)' : 'Tracker')],
                  ['Trip', (q) => (q.trip ? (q.trip.shipmentId || `Q-${q.trip.id}`) : '')],
                ]}
              />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>{['Vehicle', 'Parking area', 'Waiting', 'Trip'].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {queue.map((q) => (
                  <TableRow key={`${q.vehicleId}-${q.area}`} hover>
                    <TableCell>{q.vehicle}</TableCell>
                    <TableCell>{q.area}</TableCell>
                    <TableCell>
                      {humanMs(q.waitingMs)}
                      {q.stale && !q.byReader && <Chip size="small" label="Not reporting" color="warning" variant="outlined" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      {q.trip
                        ? <Button size="small" onClick={() => navigate(`/trips/${q.trip.id}`)}>{q.trip.shipmentId || 'Open trip'}</Button>
                        : <Chip size="small" label="No trip" variant="outlined" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>
      )}
      {!exceptionsOnly && (error ? <Alert severity="warning" sx={{ mt: 2 }}>Charts are unavailable until operational data refreshes.</Alert> : <PerformanceCharts trips={selected} issues={issues} config={data.config} speed={speed} />)}
    </PageLayout>
  );
};
export default OverviewPage;
